import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { listarFiliais, listarAreas, criarArea, atualizarArea, deletarArea, listarCodigosArea, adicionarCodigoArea, removerCodigoArea } from '../../../services/montagemPalete';
import { listarClientes } from '../../../services/clientes';
import type { Filial, AreaEntrega, AreaCodigoCliente } from '../../../types/montagemPalete';
import '../MontagemPaletes.css';

interface ClienteOption {
  id: number;
  razao_social: string;
  cnpj: string;
}

const MontagemPaleteAreas: React.FC = () => {
  const navigate = useNavigate();
  const [filiais, setFiliais] = useState<Filial[]>([]);
  const [areas, setAreas] = useState<AreaEntrega[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AreaEntrega | null>(null);
  const [form, setForm] = useState({ nome: '', codigo: '', regiao: '', descricao: '', filial_id: '' as string | number });

  // Códigos por cliente
  const [clientes, setClientes] = useState<ClienteOption[]>([]);
  const [areaExpandida, setAreaExpandida] = useState<number | null>(null);
  const [codigosCliente, setCodigosCliente] = useState<AreaCodigoCliente[]>([]);
  const [loadingCodigos, setLoadingCodigos] = useState(false);
  const [novoCodigo, setNovoCodigo] = useState({ cliente_id: '' as string | number, codigo: '' });
  const [erroCodigo, setErroCodigo] = useState('');

  const carregarAreas = useCallback(async () => {
    try {
      setLoading(true);
      const res = await listarAreas();
      if (res.success) setAreas(res.areas);
    } catch (error: any) {
      alert(error?.message || 'Erro ao carregar áreas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarAreas();
    listarFiliais().then(res => {
      if (res.success) setFiliais(res.filiais);
    }).catch((err: any) => alert(err?.message || 'Erro ao carregar filiais'));
    listarClientes({ limit: 200, ativo: 'true' }).then(res => {
      if (res.success && res.clientes) {
        setClientes(res.clientes.map(c => ({ id: c.id, razao_social: c.razao_social, cnpj: c.cnpj })));
      }
    }).catch((err: any) => alert(err?.message || 'Erro ao carregar clientes'));
  }, [carregarAreas]);

  const handleSalvar = async () => {
    try {
      if (!form.nome) {
        alert('Nome é obrigatório');
        return;
      }
      const data = { ...form, filial_id: form.filial_id ? Number(form.filial_id) : undefined };
      if (editing) {
        await atualizarArea(editing.id, data);
      } else {
        await criarArea(data);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ nome: '', codigo: '', regiao: '', descricao: '', filial_id: '' });
      carregarAreas();
    } catch (error: any) {
      alert(error?.message || 'Erro ao salvar área');
    }
  };

  const handleEditar = (area: AreaEntrega) => {
    setEditing(area);
    setForm({
      nome: area.nome,
      codigo: area.codigo || '',
      regiao: area.regiao || '',
      descricao: area.descricao || '',
      filial_id: area.filial_id || ''
    });
    setShowForm(true);
  };

  const handleDeletar = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta área de entrega?')) return;
    try {
      await deletarArea(id);
      if (areaExpandida === id) setAreaExpandida(null);
      carregarAreas();
    } catch (error: any) {
      alert(error?.message || 'Erro ao excluir área');
    }
  };

  // Códigos por cliente
  const toggleExpandirArea = async (areaId: number) => {
    if (areaExpandida === areaId) {
      setAreaExpandida(null);
      setCodigosCliente([]);
      return;
    }
    setAreaExpandida(areaId);
    setErroCodigo('');
    setNovoCodigo({ cliente_id: '', codigo: '' });
    await carregarCodigosArea(areaId);
  };

  const carregarCodigosArea = async (areaId: number) => {
    try {
      setLoadingCodigos(true);
      const res = await listarCodigosArea(areaId);
      if (res.success) setCodigosCliente(res.codigos);
    } catch (error: any) {
      setErroCodigo(error?.message || 'Erro ao carregar códigos');
    } finally {
      setLoadingCodigos(false);
    }
  };

  const handleAdicionarCodigo = async () => {
    if (!areaExpandida || !novoCodigo.cliente_id || !novoCodigo.codigo.trim()) {
      setErroCodigo('Selecione um cliente e informe o código');
      return;
    }
    setErroCodigo('');
    const codigos = novoCodigo.codigo.split(',').map(c => c.trim()).filter(c => c !== '');
    if (codigos.length === 0) {
      setErroCodigo('Informe pelo menos um código');
      return;
    }
    const erros: string[] = [];
    try {
      for (const codigo of codigos) {
        try {
          await adicionarCodigoArea(areaExpandida, {
            cliente_id: Number(novoCodigo.cliente_id),
            codigo
          });
        } catch (error: any) {
          erros.push(`${codigo}: ${error.message || 'Erro'}`);
        }
      }
      setNovoCodigo({ cliente_id: '', codigo: '' });
      await carregarCodigosArea(areaExpandida);
      if (erros.length > 0) {
        setErroCodigo(`Erros: ${erros.join('; ')}`);
      }
    } catch (error: any) {
      setErroCodigo(error.message || 'Erro ao adicionar código');
    }
  };

  const handleRemoverCodigo = async (codigoId: number) => {
    if (!areaExpandida) return;
    try {
      await removerCodigoArea(codigoId);
      await carregarCodigosArea(areaExpandida);
    } catch (error: any) {
      setErroCodigo(error?.message || 'Erro ao remover código');
    }
  };

  return (
    <div className="page-container">
      <button className="montagem-back-link" onClick={() => navigate('/montar-carga')}>
        <i className="fas fa-arrow-left"></i> Voltar
      </button>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>
            <i className="fas fa-map-marker-alt" style={{ marginRight: 8, color: '#e74c3c' }}></i>
            Áreas de Entrega ({areas.length})
          </h3>
          <button className="btn btn-primary btn-sm" onClick={() => { setShowForm(true); setEditing(null); setForm({ nome: '', codigo: '', regiao: '', descricao: '', filial_id: '' }); }}>
            <i className="fas fa-plus"></i> Nova Área
          </button>
        </div>
        <div className="card-body">
          {showForm && (
            <div className="palete-form-inline" style={{ marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '0.9rem' }}>{editing ? 'Editar Área de Entrega' : 'Nova Área de Entrega'}</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Nome *</label>
                  <input type="text" className="form-control" value={form.nome} onChange={(e) => setForm(p => ({ ...p, nome: e.target.value }))} placeholder="Ex: Zona Norte" />
                </div>
                <div className="form-group">
                  <label>Código</label>
                  <input type="text" className="form-control" value={form.codigo} onChange={(e) => setForm(p => ({ ...p, codigo: e.target.value }))} placeholder="Ex: ZN-001" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Região</label>
                  <input type="text" className="form-control" value={form.regiao} onChange={(e) => setForm(p => ({ ...p, regiao: e.target.value }))} placeholder="Ex: Norte de SP" />
                </div>
                <div className="form-group">
                  <label>Filial/Unidade</label>
                  <select className="form-control" value={form.filial_id} onChange={(e) => setForm(p => ({ ...p, filial_id: e.target.value }))}>
                    <option value="">Todas as filiais</option>
                    {filiais.map(f => (
                      <option key={f.id} value={f.id}>{f.nome_fantasia || f.razao_social}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row full">
                <div className="form-group">
                  <label>Descrição</label>
                  <input type="text" className="form-control" value={form.descricao} onChange={(e) => setForm(p => ({ ...p, descricao: e.target.value }))} placeholder="Descrição da área..." />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-outline btn-sm" onClick={() => { setShowForm(false); setEditing(null); }}>Cancelar</button>
                <button type="button" className="btn btn-primary btn-sm" onClick={handleSalvar}>
                  <i className="fas fa-save"></i> {editing ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
              <div className="loading-spinner-large"></div>
              <p>Carregando áreas...</p>
            </div>
          ) : areas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
              <i className="fas fa-map-marker-alt" style={{ fontSize: '2.5rem', color: '#ddd', display: 'block', marginBottom: 12 }}></i>
              <p>Nenhuma área de entrega cadastrada</p>
            </div>
          ) : (
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Código</th>
                    <th>Região</th>
                    <th>Filial</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {areas.map(a => (
                    <React.Fragment key={a.id}>
                      <tr>
                        <td style={{ fontWeight: 500 }}>{a.nome}</td>
                        <td style={{ fontFamily: 'monospace' }}>{a.codigo || '-'}</td>
                        <td>{a.regiao || '-'}</td>
                        <td>{a.filial_nome || '-'}</td>
                        <td>
                          <span className={`status-badge ${a.ativo ? 'status-montagem-pendente' : 'status-montagem-cancelada'}`}>
                            {a.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div className="action-buttons" style={{ justifyContent: 'flex-end' }}>
                            <button
                              className={`btn-edit`}
                              onClick={() => toggleExpandirArea(a.id)}
                              title="Códigos por Cliente"
                              style={{
                                background: areaExpandida === a.id ? '#e8f5e9' : undefined,
                                color: areaExpandida === a.id ? '#2e7d32' : undefined
                              }}
                            >
                              <i className="fas fa-tags"></i>
                            </button>
                            <button className="btn-edit" onClick={() => handleEditar(a)} title="Editar">
                              <i className="fas fa-edit"></i>
                            </button>
                            <button className="btn-delete" onClick={() => handleDeletar(a.id)} title="Excluir">
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Códigos por cliente expandidos */}
                      {areaExpandida === a.id && (
                        <tr>
                          <td colSpan={6} style={{ padding: 0, background: '#f8fafb' }}>
                            <div style={{ padding: '16px 20px', borderTop: '2px solid #3498db' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#2c3e50' }}>
                                  <i className="fas fa-tags" style={{ marginRight: 8, color: '#3498db' }}></i>
                                  Códigos por Cliente - {a.nome}
                                </h4>
                              </div>

                              {erroCodigo && (
                                <div style={{ background: '#ffebee', color: '#c62828', padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: '0.85rem' }}>
                                  <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }}></i>{erroCodigo}
                                </div>
                              )}

                              {/* Formulário para adicionar código */}
                              <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <div style={{ flex: 2, minWidth: 180 }}>
                                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#7f8c8d', marginBottom: 4 }}>Cliente</label>
                                  <select
                                    className="form-control"
                                    value={novoCodigo.cliente_id}
                                    onChange={(e) => setNovoCodigo(p => ({ ...p, cliente_id: e.target.value }))}
                                    style={{ fontSize: '0.85rem', padding: '6px 10px' }}
                                  >
                                    <option value="">Selecione o cliente</option>
                                    {clientes.map(c => (
                                      <option key={c.id} value={c.id}>{c.razao_social}</option>
                                    ))}
                                  </select>
                                </div>
                                <div style={{ flex: 1, minWidth: 120 }}>
                                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#7f8c8d', marginBottom: 4 }}>Código da Área</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={novoCodigo.codigo}
                                    onChange={(e) => setNovoCodigo(p => ({ ...p, codigo: e.target.value }))}
                                    placeholder="Ex: ZN-01 ou ZN-01,ZN-02,ZN-03"
                                    style={{ fontSize: '0.85rem', padding: '6px 10px' }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdicionarCodigo(); } }}
                                  />
                                </div>
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={handleAdicionarCodigo}
                                  disabled={!novoCodigo.cliente_id || !novoCodigo.codigo.trim()}
                                  style={{ padding: '6px 14px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                                >
                                  <i className="fas fa-plus"></i> Adicionar
                                </button>
                              </div>

                              {/* Lista de códigos */}
                              {loadingCodigos ? (
                                <div style={{ textAlign: 'center', padding: 16, color: '#7f8c8d', fontSize: '0.85rem' }}>
                                  <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Carregando códigos...
                                </div>
                              ) : codigosCliente.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: 16, color: '#95a5a6', fontSize: '0.85rem' }}>
                                  <i className="fas fa-info-circle" style={{ marginRight: 6 }}></i>
                                  Nenhum código secundário cadastrado para esta área.
                                </div>
                              ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                  <thead>
                                    <tr style={{ background: '#e8f0fe' }}>
                                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#34495e', fontSize: '0.75rem', textTransform: 'uppercase' }}>Cliente</th>
                                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#34495e', fontSize: '0.75rem', textTransform: 'uppercase' }}>CNPJ</th>
                                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#34495e', fontSize: '0.75rem', textTransform: 'uppercase' }}>Código</th>
                                      <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#34495e', fontSize: '0.75rem', textTransform: 'uppercase' }}>Ação</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {codigosCliente.map(cod => (
                                      <tr key={cod.id} style={{ borderTop: '1px solid #eee' }}>
                                        <td style={{ padding: '8px 12px' }}>{cod.cliente_razao_social || '-'}</td>
                                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '0.8rem', color: '#7f8c8d' }}>{cod.cliente_cnpj || '-'}</td>
                                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 600 }}>{cod.codigo}</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                                          <button
                                            className="btn-delete"
                                            onClick={() => handleRemoverCodigo(cod.id)}
                                            title="Remover código"
                                            style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                                          >
                                            <i className="fas fa-trash"></i>
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MontagemPaleteAreas;
